import { useEffect } from 'react';

const SEOHead = ({
  title = 'Global Trade Car - Japanese Used Cars Export Worldwide',
  description = 'Export quality Japanese used cars worldwide. Browse Toyota, Honda, Nissan and more. Competitive prices, reliable shipping, trusted by customers globally.',
  keywords = 'Japanese used cars, car export, Toyota export, Honda export, Nissan export, used car dealer, vehicle export Japan, JDM cars, right hand drive cars, affordable cars',
  ogImage = '/og-image.jpg',
  url,
  type = 'website',
  structuredData
}) => {
  const siteName = 'Global Trade Car';
  const twitterHandle = '@globaltradecar';
  const canonicalUrl = url || window.location.href;

  useEffect(() => {
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        el.setAttribute('data-seo-head', 'true');
        document.head.appendChild(el);
      }
      return el;
    };

    const ensureLink = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('link');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        el.setAttribute('data-seo-head', 'true');
        document.head.appendChild(el);
      }
      return el;
    };

    // Title
    document.title = title;

    // Primary
    ensureMeta('meta[name="title"]', { name: 'title' }).setAttribute('content', title);
    ensureMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', description);
    ensureMeta('meta[name="keywords"]', { name: 'keywords' }).setAttribute('content', keywords);
    ensureLink('link[rel="canonical"]', { rel: 'canonical' }).setAttribute('href', canonicalUrl);

    // Open Graph
    ensureMeta('meta[property="og:type"]', { property: 'og:type' }).setAttribute('content', type);
    ensureMeta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', canonicalUrl);
    ensureMeta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', title);
    ensureMeta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', description);
    ensureMeta('meta[property="og:image"]', { property: 'og:image' }).setAttribute('content', ogImage);
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name' }).setAttribute('content', siteName);

    // Twitter
    ensureMeta('meta[property="twitter:card"]', { property: 'twitter:card' }).setAttribute('content', 'summary_large_image');
    ensureMeta('meta[property="twitter:url"]', { property: 'twitter:url' }).setAttribute('content', canonicalUrl);
    ensureMeta('meta[property="twitter:title"]', { property: 'twitter:title' }).setAttribute('content', title);
    ensureMeta('meta[property="twitter:description"]', { property: 'twitter:description' }).setAttribute('content', description);
    ensureMeta('meta[property="twitter:image"]', { property: 'twitter:image' }).setAttribute('content', ogImage);
    ensureMeta('meta[property="twitter:site"]', { property: 'twitter:site' }).setAttribute('content', twitterHandle);

    // Additional
    ensureMeta('meta[name="robots"]', { name: 'robots' }).setAttribute('content', 'index, follow');
    ensureMeta('meta[name="language"]', { name: 'language' }).setAttribute('content', 'English');
    ensureMeta('meta[name="revisit-after"]', { name: 'revisit-after' }).setAttribute('content', '7 days');
    ensureMeta('meta[name="author"]', { name: 'author' }).setAttribute('content', siteName);

    // Structured data (single script tag we manage)
    const dealerSchema = {
      '@context': 'https://schema.org',
      '@type': 'AutoDealer',
      name: siteName,
      description,
      url: 'https://globaltradecar.com',
      logo: 'https://globaltradecar.com/logo.png',
      image: ogImage,
      telephone: '+81-70-9270-2242',
      email: 'sales@globaltradecar.com',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'Japan'
      },
      priceRange: '$$',
      openingHours: 'Mo-Fr 09:00-18:00',
      sameAs: [
        'https://facebook.com/globaltradecar',
        'https://instagram.com/globaltradecar',
        'https://linkedin.com/company/globaltradecar'
      ]
    };

    const payload = structuredData
      ? Array.isArray(structuredData) ? structuredData : [structuredData]
      : [];
    const scripts = [dealerSchema, ...payload];

    // Remove previously managed scripts, then write fresh (avoids duplicates)
    document.querySelectorAll('script[type="application/ld+json"][data-seo-head="true"]')
      .forEach(s => s.remove());

    scripts.forEach(schema => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-head', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [title, description, keywords, ogImage, canonicalUrl, type, structuredData ? JSON.stringify(structuredData) : null]);

  return null;
};

export default SEOHead;
